import type { StudioSystemType } from 'types/studio/items'
import { getVanDerValkPitchedCladdingOptionsFromSystem } from '../../vandervalk/applyVanDerValkPitchedCladdingOptions'
import { buildVanDerValkBuildingOutlineFromSystem } from '../../vandervalk/buildVanDerValkBuildingOutline'
import {
  buildVanDerValkFieldsFromSystem,
  buildVanDerValkPitchedFieldsFromGrids,
} from '../../vandervalk/buildVanDerValkFrames'
import {
  buildVanDerValkPitchedRoofSlopeFromModuleGrid,
  buildVanDerValkPitchedRoofSlopeFromSystem,
  vanDerValkPitchedRoofSlopeErrorMessage,
} from '../../vandervalk/buildVanDerValkPitchedRoofSlope'
import {
  fetchVanDerValkMountingBom,
  fetchVanDerValkPitchedRoofMountingBom,
  getVanDerValkBomFrameFromSystem,
  getVanDerValkFlatRoofBomProjectIndices,
  getVanDerValkMiddleZoneDeflectorForBom,
  getVanDerValkPitchedBomFrameFromSystem,
  getVanDerValkSolarRampBomFieldsFromSystem,
  isVanDerValkSolarRampSelectedOnSystem,
  getVanDerValkBomProjectIndices,
  getVanDerValkPitchedRoofWindFactors,
  getVanDerValkTopographyForBom,
  mergeVanDerValkPitchedRoofBomResults,
  type FetchVanDerValkMountingBomResult,
} from '../../vandervalk/fetchVanDerValkMountingBom'
import {
  getVanDerValkMountingTabBomIssue,
  vanDerValkMountingTabBomIssueI18nKey,
} from '../../vandervalk/getVanDerValkMountingTabBomIssue'
import {
  getVanDerValkSuitabilityPayload,
  isVanDerValkSuitabilityReadyForBom,
} from '../../vandervalk/getVanDerValkSuitabilityPayload'
import { vanDerValkApiBomLinesToMountingItems } from '../../vandervalk/mapVanDerValkVendorBomToMountingItems'
import { getVanDerValkPitchedCladdingForRoofType } from '../../vandervalk/pitchedCladding'
import {
  buildVendorCladdingOptions,
  isPitchedCladdingOptionsComplete,
  isVanDerValkPitchedCladdingType,
} from '../../vandervalk/pitchedCladdingFieldConfig'
import { refreshVanDerValkModuleGridFacetAttachments } from '../../vandervalk/refreshVanDerValkModuleGridFacetAttachments'
import {
  trackVanDerValkFlatRoofBomSuccess,
  trackVanDerValkFlatRoofError,
  trackVanDerValkPitchedBomSuccess,
  trackVanDerValkPitchedError,
  vanDerValkMountingTabBomIssueErrorType,
} from '../../vandervalk/vanDerValkAmplitude'
import {
  getVanDerValkModuleGridsForBom,
  groupVanDerValkModuleGridsByFacet,
} from '../../vandervalk/vanDerValkModuleGrids'
import { BomGeneratorAbstract } from '../bom_generators/BomGeneratorAbstract'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../MountingSystemAbstract'
import {
  BomGeneratorContext,
  CompatibilityParameters,
  MountingCalcInput,
  MountingCalcResult,
  RoofTypeName,
} from '../types'
import { addItems } from '../utils'

/** GB flat-roof roof types supported by Van der Valk flat-roof API. */
export const roofTypesCompatibleWithVanDerValk: RoofTypeName[] = [
  'Tar and Gravel / Bitumen',
  'Membrane EPDM',
  'Membrane PVC',
  'Membrane TPO',
  'Tile Concrete',
  'Flat Concrete',
]

/** GB on-roof roof types supported by Van der Valk on-roof (future API). */
export const roofTypesCompatibleWithVanDerValkOnRoof: RoofTypeName[] = [
  'Metal Standing Seam',
  'Tile Concrete',
  'Tile Clay',
  'Tile Slate',
  'Membrane EPDM',
  'Membrane PVC',
  'Membrane TPO',
  'Trapezoidal',
  'Metal Tin',
  'Corrugated Fibre Cement',
]

export function getVanDerValkRoofTypes(mountingSystemType?: string | null): RoofTypeName[] {
  return mountingSystemType === 'onRoof' ? roofTypesCompatibleWithVanDerValkOnRoof : roofTypesCompatibleWithVanDerValk
}

const spacingRuleSet: SpacingRuleSet = {
  interPanel: { horizontalSpacing: 20, verticalSpacing: 20 },
}

/** Expected local preflight outcomes — do not surface as mounting-tab warnings. */
const VANDERVALK_BOM_PREFLIGHT_MESSAGES = new Set([
  'missing suitability selection',
  'missing cladding type',
  'missing pitched cladding options',
  'missing panel layout',
  'missing roof outline',
])

/** Latest-started Van der Valk vendor BOM run wins; older in-flight completions skip merging. */
const vanDerValkVendorBomGenerationBySystemUuid = new Map<string, number>()

function bumpVanDerValkVendorBomGeneration(systemUuid: string): number {
  const next = (vanDerValkVendorBomGenerationBySystemUuid.get(systemUuid) ?? 0) + 1
  vanDerValkVendorBomGenerationBySystemUuid.set(systemUuid, next)
  return next
}

function isVanDerValkVendorBomRunStale(systemUuid: string, runId: number): boolean {
  return (vanDerValkVendorBomGenerationBySystemUuid.get(systemUuid) ?? 0) !== runId
}

class VanDerValkSpacingAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = spacingRuleSet
}

/**
 * Single roof / first block only (BOM is project-level from vendor).
 */
async function appendVanDerValkVendorBomToResult(
  input: MountingCalcInput,
  result: MountingCalcResult,
  context: BomGeneratorContext,
  blockIndex: number
): Promise<MountingCalcResult> {
  if (blockIndex !== 0 || input.roof.roofIndex !== 0) {
    return result
  }
  const system = window.editor?.selectedSystem
  if (!system) {
    return result
  }

  const tabIssue = getVanDerValkMountingTabBomIssue(system)
  if (tabIssue) {
    return result
  }

  if (!isVanDerValkSuitabilityReadyForBom(system)) {
    return result
  }

  const pre = getVanDerValkSuitabilityPayload(system)
  if (!pre.ok) {
    return result
  }

  const roofTypeName = input.roof.roofTypeName
  if (!roofTypeName) {
    return result
  }

  const topography = getVanDerValkTopographyForBom(system)
  if (!topography) {
    return result
  }

  const isPitched = system.mounting_type === 'onRoof'
  let pitchedCladdingType: string | null = null

  const runId = bumpVanDerValkVendorBomGeneration(system.uuid)
  try {
    const res = isPitched
      ? await (async () => {
          const claddingTypeRaw = getVanDerValkPitchedCladdingForRoofType(roofTypeName)
          if (!claddingTypeRaw || !isVanDerValkPitchedCladdingType(claddingTypeRaw)) {
            return { ok: false as const, message: 'missing cladding type' }
          }
          const claddingType = claddingTypeRaw
          pitchedCladdingType = claddingType
          const pitchedFrame = getVanDerValkPitchedBomFrameFromSystem(system, claddingType)
          if (!pitchedFrame) {
            return { ok: false as const, message: 'missing suitability selection' }
          }
          const pitchedCladdingOptions = getVanDerValkPitchedCladdingOptionsFromSystem(system)
          if (!isPitchedCladdingOptionsComplete(claddingType, pitchedCladdingOptions)) {
            return { ok: false as const, message: 'missing pitched cladding options' }
          }
          const pitchedIndices = getVanDerValkBomProjectIndices(system)
          const vendorCladdingOptions = buildVendorCladdingOptions(claddingType, pitchedCladdingOptions ?? {})
          const roofSlopeValidation = buildVanDerValkPitchedRoofSlopeFromSystem(system)
          if (!roofSlopeValidation.ok) {
            return {
              ok: false as const,
              message: vanDerValkPitchedRoofSlopeErrorMessage(roofSlopeValidation.reason),
            }
          }
          const gridsByFacet = groupVanDerValkModuleGridsByFacet(getVanDerValkModuleGridsForBom(system))
          const facetBomResults: Extract<FetchVanDerValkMountingBomResult, { ok: true }>[] = []
          for (const facetGrids of gridsByFacet.values()) {
            const fieldsBuilt = buildVanDerValkPitchedFieldsFromGrids(facetGrids)
            if (!fieldsBuilt.ok) {
              return { ok: false as const, message: 'missing panel layout' }
            }
            const roofSlopeBuilt = buildVanDerValkPitchedRoofSlopeFromModuleGrid(facetGrids[0])
            if (!roofSlopeBuilt.ok) {
              return {
                ok: false as const,
                message: vanDerValkPitchedRoofSlopeErrorMessage(roofSlopeBuilt.reason),
              }
            }
            const { roofPolygon, ridgeIdxs, roofType } = roofSlopeBuilt.roofSlope
            const facetResult = await fetchVanDerValkPitchedRoofMountingBom({
              ...pre.payload,
              ...pitchedIndices,
              ...getVanDerValkPitchedRoofWindFactors(system),
              ...topography,
              frame: pitchedFrame,
              claddingType,
              claddingOptions: vendorCladdingOptions,
              roofPolygon,
              ridgeIdxs,
              roofType,
              fields: fieldsBuilt.fields,
            })
            if (!facetResult.ok) {
              return facetResult
            }
            facetBomResults.push(facetResult)
          }
          return mergeVanDerValkPitchedRoofBomResults(facetBomResults)
        })()
      : await (async () => {
          const bomFrame = getVanDerValkBomFrameFromSystem(system)
          if (!bomFrame) {
            return { ok: false as const, message: 'missing suitability selection' }
          }
          const solarRamp = isVanDerValkSolarRampSelectedOnSystem(system)
          const solarRampFields = solarRamp ? getVanDerValkSolarRampBomFieldsFromSystem(system) : null
          if (solarRamp && !solarRampFields) {
            return { ok: false as const, message: 'missing solar ramp configuration' }
          }
          const fieldsBuilt = buildVanDerValkFieldsFromSystem(system, bomFrame, { solarRamp })
          if (!fieldsBuilt.ok) {
            return { ok: false as const, message: 'missing panel layout' }
          }
          const outlineBuilt = buildVanDerValkBuildingOutlineFromSystem(system)
          if (!outlineBuilt.ok) {
            return { ok: false as const, message: 'missing roof outline' }
          }
          const middleZoneDeflector = getVanDerValkMiddleZoneDeflectorForBom(system, bomFrame)
          return fetchVanDerValkMountingBom({
            ...pre.payload,
            ...getVanDerValkFlatRoofBomProjectIndices(system),
            ...topography,
            roofTypeName,
            ...bomFrame,
            middleZoneDeflector,
            ...(solarRampFields
              ? {
                  ...solarRampFields,
                  edgeCornerZoneDeflector: middleZoneDeflector as 'OPEN_SYSTEM' | 'CLOSED_SYSTEM',
                }
              : {}),
            fields: fieldsBuilt.fields,
            buildingOutline: outlineBuilt.buildingOutline,
          })
        })()
    if (isVanDerValkVendorBomRunStale(system.uuid, runId)) {
      return result
    }
    if (!res.ok) {
      const message = res.message?.trim() ?? ''
      if (!VANDERVALK_BOM_PREFLIGHT_MESSAGES.has(message)) {
        const errorMsg = message || window.translate('mount_vdv_bom_api_failed')
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message: errorMsg,
          key: 'VANDERVALK_API_WARNING',
          severity: 'warning',
          systemId: system.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        if (isPitched) {
          trackVanDerValkPitchedError({
            errorType: 'api',
            error_msg: errorMsg,
            claddingType: pitchedCladdingType,
          })
        } else {
          trackVanDerValkFlatRoofError({
            errorType: 'api',
            error_msg: errorMsg,
          })
        }
      }
      return result
    }
    if (res.bom.length === 0) {
      return result
    }

    const vendorItems = vanDerValkApiBomLinesToMountingItems(res.bom)
    const merged = addItems(result, vendorItems)
    if (isPitched) {
      trackVanDerValkPitchedBomSuccess(merged, system, {
        roofType: roofTypeName,
        claddingType: pitchedCladdingType,
      })
    } else {
      trackVanDerValkFlatRoofBomSuccess(merged, system, {
        roofType: roofTypeName,
      })
    }
    return merged
  } catch (ex: any) {
    if (!isVanDerValkVendorBomRunStale(system.uuid, runId)) {
      const errorMsg = window.translate('BOM generation failed')
      context.errorHandling.http(ex, { msg: errorMsg })
      window.Designer?.showNotification?.(errorMsg, 'warning')
      if (isPitched) {
        trackVanDerValkPitchedError({
          errorType: 'api',
          error_msg: errorMsg,
          claddingType: pitchedCladdingType,
        })
      } else {
        trackVanDerValkFlatRoofError({
          errorType: 'api',
          error_msg: errorMsg,
        })
      }
    }
    return result
  }
}

class VanDerValkBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult, context?: BomGeneratorContext): Promise<MountingCalcResult> {
    if (!context) {
      return result
    }
    return appendVanDerValkVendorBomToResult(this.input, result, context, this.blockIndex)
  }
}

/**
 * Surfaces roof outline / BOM layout issues in the design-tab mounting warning box
 * (see SolarNestGroundMount.validateSolarNestGroundMountCompatibility).
 */
export function validateVanDerValkCompatibility(system: StudioSystemType): boolean {
  const mountingName = system?.mounting
  if (mountingName !== 'vanDerValk') {
    return true
  }

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
    system.uuid,
    'plugin'
  )
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    `${mountingName.toUpperCase()}_INPUT_WARNING`,
    system.uuid,
    'plugin'
  )
  window.WorkspaceHelper.removeProjectErrorFromReduxStore('VANDERVALK_API_WARNING', system.uuid, 'plugin')

  refreshVanDerValkModuleGridFacetAttachments(system)

  const issue = getVanDerValkMountingTabBomIssue(system)
  if (!issue) {
    return true
  }

  const key =
    issue.kind === 'outline' || issue.kind === 'roof_structure'
      ? `${mountingName.toUpperCase()}_PLUGIN_WARNING`
      : `${mountingName.toUpperCase()}_INPUT_WARNING`

  const errorMsg = window.translate(vanDerValkMountingTabBomIssueI18nKey(issue))
  window.WorkspaceHelper.addProjectErrorToReduxStore({
    message: errorMsg,
    key,
    severity: 'warning',
    systemId: system.uuid,
    source: 'plugin',
    category: 'mounting',
    options: {},
  })
  if (system.mounting_type === 'onRoof') {
    trackVanDerValkPitchedError({
      errorType: vanDerValkMountingTabBomIssueErrorType(issue),
      error_msg: errorMsg,
    })
  } else {
    trackVanDerValkFlatRoofError({
      errorType: vanDerValkMountingTabBomIssueErrorType(issue),
      error_msg: errorMsg,
    })
  }
  return true
}

/** Flat-roof Van der Valk vendor suitability and combined ballast/fixation BOM. */
export class VanDerValk extends MountingSystemAbstract {
  layoutAdjusterClass = VanDerValkSpacingAdjuster
  bomGeneratorClass = VanDerValkBomGenerator

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      roofTypeRequired: true,
      roofTypes: roofTypesCompatibleWithVanDerValk,
    }
  }

  /**
   * When `panelBlocks` is empty, `super.generateBom` never instantiates the BOM generator. Still fetch vendor BOM.
   */
  async generateBom(result: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    result = await super.generateBom(result, context)
    if (result.panelBlocks.length === 0) {
      return appendVanDerValkVendorBomToResult(this.input, result, context, 0)
    }
    return result
  }
}

export const __PRIVATE__VanDerValkBomForTests = {
  VanDerValkBomGenerator,
  VanDerValkSpacingAdjuster,
  bumpVanDerValkVendorBomGeneration,
  isVanDerValkVendorBomRunStale,
  appendVanDerValkVendorBomToResult,
  VANDERVALK_BOM_PREFLIGHT_MESSAGES,
  resetVanDerValkVendorBomGenerationForTests: () => vanDerValkVendorBomGenerationBySystemUuid.clear(),
}
